import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { TAG_FILES_FOR_DATA_SOURCE } from "../DataTagConstants";
import { categoryIcons } from "../../../components/common/utilities";
import { CATEGORY_LOOKUP, SENSITIVITY_LOOKUP } from "./DataTagAllFilesTable";
import "./TagFilesDialog.scss";

export const EMPTY_SELECTION = {
  sensitivities: [],
  categories: [],
  regulations: [],
  fileIds: [],
};

// same colours as the sensitivity rings / regulation badges in the quick filters
const SENSITIVITY_OPTIONS = [
  { key: "CRITICAL", color: "#e53935" },
  { key: "HIGH", color: "#f4511e" },
  { key: "MEDIUM", color: "#fb8c00" },
  { key: "LOW", color: "#081981" },
  { key: "NO_RISK", color: "#650176" },
];

const REGULATION_COLORS = {
  CCPA: "#1f5fbf",
  GDPR: "#e07a1f",
  PCI_DSS: "#2e9e5b",
  PII: "#d93025",
};
const FALLBACK_COLORS = ["#7c5cf0", "#00897b", "#6d4c41", "#c2185b", "#455a64"];
const regulationColor = (name) =>
  REGULATION_COLORS[name] ||
  FALLBACK_COLORS[
    Array.from(name).reduce((n, ch) => n + ch.charCodeAt(0), 0) %
      FALLBACK_COLORS.length
  ];

const HIDDEN_CATEGORIES = ["NOT_PROCESSED", "NO_SENSITIVITY"];

export default function TagFilesDialog({
  selected,
  onChange,
  onClose,
  onSuccess,
  onPickFiles,
}) {
  const dispatch = useDispatch();

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );
  const status = useSelector((state) => state.dataTagReducer.tagFilesStatus);
  const classificationStats = useSelector(
    (state) => state.dataTagReducer.sensitiveCardData?.fileClassificationStatus,
  );
  const categoryCardData = useSelector(
    (state) => state.dataTagReducer.categoryCardData,
  );

  const regulationOptions = useMemo(() => {
    const set = new Set();
    (classificationStats || []).forEach((item) => {
      (item["FileClassificationStats.classificationCombination"] || "")
        .split(",")
        .forEach((c) => c.trim() && set.add(c.trim()));
    });
    return Array.from(set);
  }, [classificationStats]);

  const categoryOptions = useMemo(() => {
    const fromData = (categoryCardData || [])
      .map((r) => r["DataTagSensitivity.finalType"])
      .filter((c) => c && !HIDDEN_CATEGORIES.includes(c));
    return fromData.length > 0 ? fromData : Object.keys(CATEGORY_LOOKUP);
  }, [categoryCardData]);

  const toggle = (key, value) =>
    onChange({
      ...selected,
      [key]: selected[key].includes(value)
        ? selected[key].filter((v) => v !== value)
        : [...selected[key], value],
    });

  const loading = !!status?.loading;
  const done = status?.status === "success";
  const total =
    selected.sensitivities.length +
    selected.categories.length +
    selected.regulations.length +
    selected.fileIds.length;

  // close (and clear the selection) shortly after a successful request
  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(onSuccess, 1200);
    return () => clearTimeout(t);
  }, [done]);

  const handleTag = () =>
    dispatch({
      type: TAG_FILES_FOR_DATA_SOURCE,
      payload: {
        dataSourceId,
        sensitivities: selected.sensitivities,
        // the API still names this parameter "fileTypes"
        fileTypes: selected.categories,
        regulations: selected.regulations,
        fileIds: selected.fileIds,
      },
    });

  const summary = [
    [selected.sensitivities.length, "sensitivity"],
    [selected.categories.length, "category"],
    [selected.regulations.length, "regulation"],
    [selected.fileIds.length, "file"],
  ]
    .filter(([n]) => n > 0)
    .map(([n, label]) =>
      label === "category"
        ? `${n} ${n > 1 ? "categories" : "category"}`
        : `${n} ${label}${n > 1 ? "s" : ""}`,
    )
    .join(" · ");

  return (
    <Dialog
      open
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: "tf-paper" }}
    >
      <div className="tf-header">
        <div>
          <div className="tf-title">Tag Files</div>
          <div className="tf-subtitle">
            Pick what to tag. Files must match every option you choose.
          </div>
        </div>
        <button
          type="button"
          className="tf-close"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
        >
          <CloseIcon style={{ fontSize: 20 }} />
        </button>
      </div>

      <div className="tf-body">
        {/* ---------- Sensitivity ---------- */}
        <div className="tf-section">
          <div className="tf-label">Sensitivity</div>
          <div className="tf-options">
            {SENSITIVITY_OPTIONS.map(({ key, color }) => {
              const on = selected.sensitivities.includes(key);
              return (
                <button
                  type="button"
                  key={key}
                  className={`tf-opt ${on ? "on" : ""}`}
                  style={{ "--c": color }}
                  onClick={() => toggle("sensitivities", key)}
                >
                  <span className="tf-ring" />
                  {SENSITIVITY_LOOKUP[key]?.label || key}
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------- Category ---------- */}
        <div className="tf-section">
          <div className="tf-label">Category</div>
          <div className="tf-options">
            {categoryOptions.map((key) => {
              const on = selected.categories.includes(key);
              return (
                <button
                  type="button"
                  key={key}
                  className={`tf-opt ${on ? "on" : ""}`}
                  style={{ "--c": "#7c5cf0" }}
                  onClick={() => toggle("categories", key)}
                >
                  {categoryIcons[key] && (
                    <img src={categoryIcons[key]} alt="" className="tf-cat-icon" />
                  )}
                  {CATEGORY_LOOKUP[key]?.label || key}
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------- Regulations ---------- */}
        <div className="tf-section">
          <div className="tf-label">Regulations</div>
          <div className="tf-options">
            {regulationOptions.length === 0 && (
              <span className="tf-none">No regulations found.</span>
            )}
            {regulationOptions.map((name) => {
              const on = selected.regulations.includes(name);
              return (
                <button
                  type="button"
                  key={name}
                  className={`tf-reg ${on ? "on" : ""}`}
                  style={{ "--c": regulationColor(name) }}
                  onClick={() => toggle("regulations", name)}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------- Files (picked in the All Files table itself) ---------- */}
        <div className="tf-section">
          <div className="tf-label">
            Files
            <span className="tf-label-note">optional</span>
          </div>
          <div className="tf-files-pick">
            <span className="tf-files-count">
              {selected.fileIds.length === 0
                ? "No files picked."
                : `${selected.fileIds.length} ${
                    selected.fileIds.length === 1 ? "file" : "files"
                  } selected`}
            </span>
            <button type="button" className="tf-secondary" onClick={onPickFiles}>
              {selected.fileIds.length === 0
                ? "Select files in table"
                : "Change selection in table"}
            </button>
            {selected.fileIds.length > 0 && (
              <button
                type="button"
                className="tf-link"
                onClick={() => onChange({ ...selected, fileIds: [] })}
              >
                Clear files
              </button>
            )}
          </div>
          <div className="tf-note">
            Opens the All Files table, where you can search by file name, change
            the page size and tick the files you want.
          </div>
        </div>
      </div>

      <div className="tf-footer">
        <div className="tf-summary">
          {status?.status === "error" && (
            <span className="tf-msg error">{status.message}</span>
          )}
          {done && <span className="tf-msg ok">{status.message}</span>}
          {!done && status?.status !== "error" && (
            <span>{total === 0 ? "Nothing selected yet." : summary}</span>
          )}
        </div>
        <button
          type="button"
          className="tf-secondary"
          onClick={() => onChange(EMPTY_SELECTION)}
          disabled={total === 0 || loading}
        >
          Clear
        </button>
        <button
          type="button"
          className="tf-primary"
          onClick={handleTag}
          disabled={total === 0 || loading || done}
        >
          {loading && <CircularProgress size={14} style={{ color: "#fff" }} />}
          {loading ? "Tagging..." : "Tag Files"}
        </button>
      </div>
    </Dialog>
  );
}